import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, List, ListItem, ListItemButton, CircularProgress, Alert
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../hooks/usePermission';
import { UserType } from '../../interfaces/userType.enum';
import { OrphanPunch } from '../../interfaces/shift/OrphanPunch';
import { RosterCell } from '../../interfaces/shift/RosterCell';
import { OrphanPunchService } from '../../services/orphan-punch.service';
import { UserService } from '../../services/user.service';
import { ShiftRosterService } from '../../services/shift-roster.service';
import { isInstanceStatus } from './instanceStatus';

interface UserMap {
    [userId: string]: string;
}

interface DialogState {
    open: boolean;
    orphan: OrphanPunch | null;
    loading: boolean;
    candidates: RosterCell[];
    selectedInstanceId: string | null;
    attachingOrSaving: boolean;
}

const formatLocalTime = (isoString: string): string => {
    try {
        const date = new Date(isoString);
        return date.toLocaleString();
    } catch {
        return isoString;
    }
};

const subtractDays = (dateStr: string, days: number): string => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() - days);
    return d.toISOString().split('T')[0];
};

const addDays = (dateStr: string, days: number): string => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
};

interface AttachOrphanDialogProps {
    open: boolean;
    onClose: () => void;
    orphan: OrphanPunch | null;
    institutionId: string;
    onSaved: () => void;
    toast: any;
}

const AttachOrphanDialog: React.FC<AttachOrphanDialogProps> = ({
    open, onClose, orphan, institutionId, onSaved, toast
}) => {
    const [loading, setLoading] = useState(false);
    const [candidates, setCandidates] = useState<RosterCell[]>([]);
    const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
    const [attaching, setAttaching] = useState(false);
    const { t } = useTranslation(['shifts', 'common']);

    const { user } = useAuth();
    const canAssign = usePermission('SHIFT_ASSIGN', user?.roles.includes(UserType.InstitutionAdmin) ?? false);

    useEffect(() => {
        if (open && orphan) {
            fetchCandidates();
            setSelectedInstanceId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, orphan?.id]);

    const fetchCandidates = async () => {
        if (!orphan) return;
        setLoading(true);
        try {
            const punchDate = orphan.punchTimestamp.split('T')[0];
            const from = subtractDays(punchDate, 1);
            const to = addDays(punchDate, 1);
            const roster = await ShiftRosterService.getRoster(institutionId, from, to, orphan.userId);
            setCandidates(roster);
        } catch (err) {
            toast.showApiError(err, t('orphans.loadCandidatesFailed'));
            setCandidates([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAttach = async () => {
        if (!orphan || !selectedInstanceId) return;
        setAttaching(true);
        try {
            await OrphanPunchService.attach(institutionId, orphan.id, { instanceId: selectedInstanceId });
            const selected = candidates.find(c => c.instanceId === selectedInstanceId);
            const shiftDisplay = selected?.shiftCode || t('orphans.unknown');
            const dateDisplay = selected?.businessDate || '?';
            toast.showSuccess(t('orphans.attached', { shift: shiftDisplay, date: dateDisplay }));
            onSaved();
            onClose();
        } catch (err: any) {
            const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || t('orphans.unknownError');
            let displayMsg: string;

            if (msg.includes('Orphan punch not found')) {
                displayMsg = t('orphans.errors.notFound');
            } else if (msg.includes('ORPHAN_ALREADY_RESOLVED')) {
                displayMsg = t('orphans.errors.alreadyResolved');
            } else if (msg.includes('CROSS_INSTITUTION_REFERENCE')) {
                displayMsg = t('orphans.errors.crossInstitution');
            } else if (msg.includes('INSTANCE_USER_MISMATCH')) {
                displayMsg = t('orphans.errors.userMismatch');
            } else {
                displayMsg = t('orphans.errors.attachFailed', { message: msg });
            }

            toast.showApiError(err, displayMsg);
        } finally {
            setAttaching(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('orphans.attachTitle')}</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {orphan && (
                    <Box sx={{ mb: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="caption" display="block">
                            <strong>{t('orphans.punchLabel')}</strong> {formatLocalTime(orphan.punchTimestamp)}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>{t('orphans.sourceLabel')}</strong> {orphan.source}
                        </Typography>
                    </Box>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : candidates.length === 0 ? (
                    <Alert severity="info">
                        {t('orphans.noCandidates')}
                    </Alert>
                ) : (
                    <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1 }}>
                        {candidates.map((cell) => (
                            <ListItem key={cell.instanceId} disablePadding>
                                <ListItemButton
                                    selected={selectedInstanceId === cell.instanceId}
                                    onClick={() => setSelectedInstanceId(cell.instanceId)}
                                >
                                    <Box sx={{ width: '100%' }}>
                                        <Typography variant="body2">
                                            {cell.businessDate} · {cell.shiftCode || t('orphans.noShift')} · {isInstanceStatus(cell.status) ? t(`instanceStatus.${cell.status}`) : cell.status}
                                        </Typography>
                                    </Box>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
                <Button
                    onClick={handleAttach}
                    disabled={!canAssign || !selectedInstanceId || loading || attaching || candidates.length === 0}
                    variant="contained"
                >
                    {attaching ? t('orphans.attaching') : t('orphans.attach')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const OrphanPunchInbox: React.FC = () => {
    const { user, selectedInstitutionId } = useAuth();
    const toast = useToast();
    const { t } = useTranslation(['shifts', 'common']);

    const canView = usePermission('SHIFT_VIEW_ALL', user?.roles.includes(UserType.InstitutionAdmin) ?? false);
    const canAssign = usePermission('SHIFT_ASSIGN', user?.roles.includes(UserType.InstitutionAdmin) ?? false);

    const [orphans, setOrphans] = useState<OrphanPunch[]>([]);
    const [userMap, setUserMap] = useState<UserMap>({});
    const [loading, setLoading] = useState(true);
    const [dialogState, setDialogState] = useState<DialogState>({
        open: false,
        orphan: null,
        loading: false,
        candidates: [],
        selectedInstanceId: null,
        attachingOrSaving: false,
    });

    useEffect(() => {
        if (selectedInstitutionId && canView) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionId, canView]);

    const fetchData = async () => {
        if (!selectedInstitutionId) return;
        setLoading(true);
        try {
            const [orphanData, usersResp] = await Promise.all([
                OrphanPunchService.listUnresolved(selectedInstitutionId),
                UserService.getInstitutionUsers(0, 200, selectedInstitutionId),
            ]);

            setOrphans(orphanData);

            const map: UserMap = {};
            const users = usersResp.content || [];
            users.forEach((u: any) => {
                map[u.id] = u.name || u.username || u.id;
            });
            setUserMap(map);
        } catch (err) {
            toast.showApiError(err, t('orphans.fetchFailed'));
            setOrphans([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        fetchData();
    };

    const handleDiscard = async (orphan: OrphanPunch) => {
        if (!selectedInstitutionId) return;
        if (!window.confirm(t('orphans.confirmDiscard', { time: formatLocalTime(orphan.punchTimestamp) }))) {
            return;
        }
        try {
            await OrphanPunchService.discard(selectedInstitutionId, orphan.id);
            toast.showSuccess(t('orphans.discarded'));
            await fetchData();
        } catch (err) {
            toast.showApiError(err, t('orphans.discardFailed'));
        }
    };

    const handleOpenAttachDialog = (orphan: OrphanPunch) => {
        setDialogState({
            open: true,
            orphan,
            loading: false,
            candidates: [],
            selectedInstanceId: null,
            attachingOrSaving: false,
        });
    };

    const handleCloseDialog = () => {
        setDialogState({
            ...dialogState,
            open: false,
            orphan: null,
        });
    };

    const handleDialogSaved = () => {
        fetchData();
    };

    if (!canView) {
        return (
            <Box sx={{ padding: 3 }}>
                <Typography color="error">{t('orphans.noPermission')}</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ padding: 3 }}>
                <Typography>{t('loading')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('orphans.title')}</Typography>
                <IconButton onClick={handleRefresh} title={t('common:actions.refresh')}>
                    <RefreshIcon />
                </IconButton>
            </Box>

            {orphans.length === 0 ? (
                <Typography>{t('orphans.empty')}</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell>{t('orphans.columns.punchTime')}</TableCell>
                                <TableCell>{t('orphans.columns.user')}</TableCell>
                                <TableCell>{t('orphans.columns.source')}</TableCell>
                                <TableCell>{t('common:fields.reason')}</TableCell>
                                <TableCell>{t('orphans.columns.deviceId')}</TableCell>
                                <TableCell align="right">{t('common:fields.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orphans.map((orphan) => (
                                <TableRow key={orphan.id}>
                                    <TableCell>{formatLocalTime(orphan.punchTimestamp)}</TableCell>
                                    <TableCell>{userMap[orphan.userId] || orphan.userId}</TableCell>
                                    <TableCell>
                                        <Chip label={orphan.source} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={orphan.reason} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>{orphan.deviceId || '—'}</TableCell>
                                    <TableCell align="right">
                                        {canAssign && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenAttachDialog(orphan)}
                                                    title={t('orphans.attachToShift')}
                                                >
                                                    <LinkIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDiscard(orphan)}
                                                    title={t('orphans.discard')}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {dialogState.orphan && (
                <AttachOrphanDialog
                    open={dialogState.open}
                    onClose={handleCloseDialog}
                    orphan={dialogState.orphan}
                    institutionId={selectedInstitutionId || ''}
                    onSaved={handleDialogSaved}
                    toast={toast}
                />
            )}
        </Box>
    );
};

export default OrphanPunchInbox;
